import { Box, Grid, MenuItem } from '@mui/material';
import Text from 'components/text';
import { useEffect, useState } from 'react';
import api from 'utils/api';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import copyToClipboard from 'utils/copyToClipboard';
import { Check, EyeOff, Pencil, X } from 'lucide-react';
import Card from 'components/card';
import Row from 'components/row';
import BaseInput from 'components/inputs/base-input';
import star from 'assets/icons/star.svg';
import { youtubeUrl } from '../../../../utils/youtube';

export interface InvitationInterface {
  id: number;
  token: string;
  dealid: number | null;
  status: string;
  artistid: number;
  inviterid: number;
  createdat: string;
  spotifyid: string;
  youtubeid: string;
  magiclink: string;
  artistname: string;
  termlength: number;
  artistemail: string;
  invitername: string;
  advanceamount: number;
  analyticsaccess: boolean;
  educationaccess: boolean;
  images: { url: string }[] | null;
  distributionstatus: string;
}

// Values must match the backend's DistributionStatus enum (update-invitation.dto.ts) — others get a 400;
// labels mirror the New Invitation form's select
const DISTRIBUTION_STATUSES = [
  { value: 'redistributed', label: 'Re-distributed to Snafu' },
  { value: 'other', label: 'Other' },
];
const YES_NO = [
  { value: 'YES', label: 'YES' },
  { value: 'NO', label: 'NO' },
];

interface InvitationDraft {
  advanceamount: string;
  termlength: string;
  distributionstatus: string;
  educationaccess: boolean;
  analyticsaccess: boolean;
}

const getImage = (images: { url: string }[] | null) => {
  if (images && images.length > 0) {
    return images[0].url;
  }
  return null;
};

const actionSx = {
  cursor: 'pointer',
  userSelect: 'none',
  opacity: 0.7,
  color: '#111',
  display: 'flex',
  alignItems: 'center',
  '&:hover': { opacity: 1 },
} as const;

const editInputProps = {
  variant: 'filled',
  hiddenLabel: true,
  size: 'small',
  error: false,
} as const;

const EditNumber = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
  <BaseInput
    {...editInputProps}
    type="number"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    sx={{ width: 140 }}
    inputProps={{ style: { textAlign: 'right' } }}
  />
);

const EditSelect = ({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) => (
  <BaseInput
    {...editInputProps}
    select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    sx={{ minWidth: 160 }}
  >
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ))}
  </BaseInput>
);

export default function InvitedRow({
  invite,
  open,
  onToggle,
  canEdit,
  onSaved,
  onToast,
}: {
  invite: InvitationInterface;
  open: boolean;
  onToggle: () => void;
  canEdit: boolean;
  onSaved: (id: number, patch: Partial<InvitationInterface>) => void;
  onToast: (message: string) => void;
}) {
  const [draft, setDraft] = useState<InvitationDraft | null>(null);
  const [saving, setSaving] = useState(false);

  const editing = draft !== null;

  // Expanding another invitation collapses this row — discard any in-progress edit with it
  useEffect(() => {
    if (!open) setDraft(null);
  }, [open]);

  const isValidDistributionStatus =
    !!draft && DISTRIBUTION_STATUSES.some((option) => option.value === draft.distributionstatus);

  const distributionOptions =
    !draft || isValidDistributionStatus
      ? DISTRIBUTION_STATUSES
      : [
          { value: draft.distributionstatus, label: draft.distributionstatus.toUpperCase() },
          ...DISTRIBUTION_STATUSES,
        ];

  const startEdit = () => {
    setDraft({
      advanceamount: invite.advanceamount.toString(),
      termlength: invite.termlength.toString(),
      distributionstatus: invite.distributionstatus,
      educationaccess: invite.educationaccess,
      analyticsaccess: invite.analyticsaccess,
    });
    if (!open) onToggle();
  };

  const cancelEdit = () => setDraft(null);

  const saveEdit = () => {
    if (!draft || saving) return;

    const patch: Partial<InvitationInterface> = {
      advanceamount: Number(draft.advanceamount) || 0,
      termlength: Number(draft.termlength) || 0,
      educationaccess: draft.educationaccess,
      analyticsaccess: draft.analyticsaccess,
    };
    // Legacy values outside the enum are shown but can't be submitted — omit to keep them unchanged
    if (isValidDistributionStatus) {
      patch.distributionstatus = draft.distributionstatus;
    }

    setSaving(true);
    api.invitation
      .updateInvitation({
        id: invite.id,
        data: {
          advanceAmount: patch.advanceamount,
          termLength: patch.termlength,
          distributionStatus: patch.distributionstatus,
          educationAccess: patch.educationaccess,
          analyticsAccess: patch.analyticsaccess,
        },
      })
      .then(() => {
        onSaved(invite.id, patch);
        setDraft(null);
        onToast('Invitation updated');
      })
      .catch((err: { message?: string }) => {
        onToast(typeof err?.message === 'string' ? err.message : 'Failed to update invitation');
      })
      .finally(() => setSaving(false));
  };

  return (
    <Box borderBottom={1} borderColor="#ddd" paddingBottom={2}>
      <Box display="flex" gap={2}>
        <Box border={0}>
          <IconButton aria-label="expand row" size="small" disabled={editing} onClick={onToggle}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>
        <Box>
          <img
            src={getImage(invite.images) || star}
            alt="artist"
            width="40"
            height="40"
            style={{ borderRadius: '22px', minWidth: '40px' }}
          />
        </Box>
        <Box flexGrow={1}>
          <Box
            marginBottom={0.6}
            display="flex"
            gap={2}
            height={40}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" gap={1.5}>
              <Box
                width={20}
                height={20}
                bgcolor={invite.status === 'accepted' ? '#12a621' : '#555'}
                borderRadius={10}
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="#fff"
              >
                {
                  {
                    accepted: <Check size={16} />,
                    pending: <EyeOff size={16} />,
                  }[invite.status]
                }
              </Box>
              <Text fontSize={18}>{invite.id}</Text>
              <Text fontSize={18}>{invite.artistname}</Text>
            </Box>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1.5}>
              {editing ? (
                <>
                  <Box
                    title="Save changes"
                    onClick={saveEdit}
                    sx={{ ...actionSx, '&:hover': { opacity: 1, color: '#12a621' } }}
                  >
                    <Check size={18} />
                  </Box>
                  <Box title="Discard changes" onClick={cancelEdit} sx={actionSx}>
                    <X size={18} />
                  </Box>
                </>
              ) : (
                <>
                  {canEdit && (
                    <Box title="Edit details" onClick={startEdit} sx={actionSx}>
                      <Pencil size={16} />
                    </Box>
                  )}
                  <Box
                    flexDirection="row"
                    gap={0.5}
                    onClick={() => {
                      onToast('Magic link copied to clipboard!');
                      copyToClipboard(invite.magiclink);
                    }}
                    sx={actionSx}
                  >
                    <ContentCopyIcon sx={{ fontSize: 16, color: '#111' }} />
                    <Text fontSize={16} color="#000" display={{ xs: 'none', sm: 'block' }}>
                      Copy Magic Link
                    </Text>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Grid
          container
          spacing={2}
          paddingY={2}
          paddingLeft={{
            xs: 0,
            lg: 6,
          }}
        >
          <Grid item xs={12} md={6}>
            <Card>
              <Card.Head>Artist Details</Card.Head>
              <Card.Body>
                <Row title="Email" value={invite.artistemail} />
                {invite.spotifyid && <Row title="Spotify" value={invite.spotifyid} />}
                {invite.youtubeid && (
                  <Row
                    title="Youtube"
                    value={
                      youtubeUrl(invite.youtubeid) !== null ? (
                        <a
                          target="_blank"
                          href={youtubeUrl(invite.youtubeid) || ''}
                          rel="noreferrer"
                        >
                          {invite.artistname}
                        </a>
                      ) : (
                        <>{invite.youtubeid}</>
                      )
                    }
                  />
                )}
                <Row
                  title="Advance Amount"
                  value={
                    draft ? (
                      <EditNumber
                        value={draft.advanceamount}
                        onChange={(advanceamount) => setDraft({ ...draft, advanceamount })}
                      />
                    ) : (
                      invite.advanceamount.toString()
                    )
                  }
                />
                <Row
                  title="Term Length"
                  value={
                    draft ? (
                      <EditNumber
                        value={draft.termlength}
                        onChange={(termlength) => setDraft({ ...draft, termlength })}
                      />
                    ) : (
                      invite.termlength.toString()
                    )
                  }
                />
                <Row
                  title="Distribution Status"
                  value={
                    draft ? (
                      <EditSelect
                        value={draft.distributionstatus}
                        options={distributionOptions}
                        onChange={(distributionstatus) =>
                          setDraft({ ...draft, distributionstatus })
                        }
                      />
                    ) : (
                      invite.distributionstatus.toUpperCase()
                    )
                  }
                />
              </Card.Body>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <Card.Head>Invitation Details</Card.Head>
              <Card.Body>
                <Row title="Status" value={invite.status.toUpperCase()} />
                <Row title="Invited By" value={invite.invitername} />
                <Row title="Invited At" value={invite.createdat.split('T')[0]} />
                <Row
                  title="Education"
                  value={
                    draft ? (
                      <EditSelect
                        value={draft.educationaccess ? 'YES' : 'NO'}
                        options={YES_NO}
                        onChange={(value) =>
                          setDraft({ ...draft, educationaccess: value === 'YES' })
                        }
                      />
                    ) : invite.educationaccess ? (
                      'YES'
                    ) : (
                      'NO'
                    )
                  }
                />
                <Row
                  title="Analytics"
                  value={
                    draft ? (
                      <EditSelect
                        value={draft.analyticsaccess ? 'YES' : 'NO'}
                        options={YES_NO}
                        onChange={(value) =>
                          setDraft({ ...draft, analyticsaccess: value === 'YES' })
                        }
                      />
                    ) : invite.analyticsaccess ? (
                      'YES'
                    ) : (
                      'NO'
                    )
                  }
                />
              </Card.Body>
            </Card>
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );
}
